import { Node, mergeAttributes } from "@tiptap/core";
import { ReactNodeViewRenderer } from "@tiptap/react";

import { FileNodeView } from "../nodeViews/FileNodeView";

export type FileAttrs = { src: string; name: string };

export type FileOptions = { onClick?: (src: string, attrs: FileAttrs) => void };

declare module "@tiptap/core" {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface Commands<ReturnType> {
    file: {
      insertFile: (attrs: { name: string; src: string }) => ReturnType;
      updateFile: (attrs: Partial<FileAttrs>) => ReturnType;
      removeFile: () => ReturnType;
    };
  }
}

export const File = Node.create<FileOptions>({
  name: "file",

  priority: 1100,

  group: "inline",

  inline: true,

  atom: true,

  selectable: true,

  addOptions() {
    return {};
  },

  addAttributes() {
    return {
      src: {
        default: "",
        parseHTML: (element) =>
          element.getAttribute("href") ||
          element.getAttribute("data-url") ||
          element.getAttribute("src") ||
          "",
        renderHTML: (attributes) => ({ href: attributes.src as string }),
      },
      name: {
        default: "",
        parseHTML: (element) =>
          element.getAttribute("data-name") ||
          element.getAttribute("name") ||
          element.textContent ||
          "",
        renderHTML: (attributes) => ({
          "data-name": ((attributes.name as string) || "").trim(),
        }),
      },
    };
  },

  parseHTML() {
    // Span kept for content saved before file chips became anchors.
    // priority > Link's default parse rule (50) so file anchors win over
    // a[href] — extension priority alone does not set parseDOM priority.
    return [
      { tag: 'a[data-ext="file"]', priority: 60 },
      { tag: 'span[data-ext="file"]', priority: 60 },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    return [
      "a",
      mergeAttributes(HTMLAttributes, {
        "data-ext": "file",
        href: node.attrs.src as string,
        rel: "noopener noreferrer",
        target: "_blank",
      }),
      ((node.attrs.name as string) || "").trim(),
    ];
  },

  renderText({ node }) {
    return ((node.attrs.name as string) || "").trim();
  },

  addNodeView() {
    return ReactNodeViewRenderer(FileNodeView);
  },

  addCommands() {
    return {
      insertFile:
        (attrs) =>
        ({ commands }) =>
          commands.insertContent({ type: this.name, attrs }),
      updateFile:
        (attrs) =>
        ({ commands }) =>
          commands.updateAttributes(this.name, attrs),
      removeFile:
        () =>
        ({ commands, editor }) => {
          if (!editor.isActive(this.name)) {
            return false;
          }
          return commands.deleteSelection();
        },
    };
  },
});
